import { useMemo } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type {
  FormDescriptor,
  FormMode,
  ResolvedExperienceShell,
  ResolvedResourceExperienceDescriptor,
  ResolvedRouteExperienceDescriptor,
  TableDescriptor,
} from '@finance/contracts';
import { validateResolvedDescriptorI18n } from '../../i18n/descriptor-closure';
import { useSession } from '../auth/session-provider';
import {
  type ExperienceFingerprint,
  experienceQueryKeys,
  fetchExperienceRoutes,
  fetchExperienceShell,
  fetchResolvedForm,
  fetchResolvedResource,
  fetchResolvedTable,
  getResolvedResourceRecord,
  listResolvedResource,
  type GenericResourceListQuery,
  type GenericResourceListResponse,
} from './experience-api';

const experienceQueryDefaults = {
  staleTime: 60_000,
  gcTime: 10 * 60_000,
  retry: false,
  refetchOnWindowFocus: false,
} as const;

function useExperienceFingerprint(): ExperienceFingerprint {
  const { user, currentContext } = useSession();
  return useMemo(
    () => ({
      membershipId: currentContext?.membershipId,
      organizationId: currentContext?.organizationId,
      branchId: currentContext?.branchId,
      clientAccountId: currentContext?.clientAccountId,
      portfolioAccountId: currentContext?.portfolioAccountId,
      persona: user?.persona,
      scopeKind: user?.scopeKind,
      aal: user?.aal,
      entitlementsVersion: user?.entitlementsVersion,
      delegated: user?.delegated,
      delegationId: user?.delegationId,
    }),
    [
      currentContext?.membershipId,
      currentContext?.organizationId,
      currentContext?.branchId,
      currentContext?.clientAccountId,
      currentContext?.portfolioAccountId,
      user?.persona,
      user?.scopeKind,
      user?.aal,
      user?.entitlementsVersion,
      user?.delegated,
      user?.delegationId,
    ],
  );
}

export function useExperienceShell(enabled = true) {
  const fp = useExperienceFingerprint();
  return useQuery<ResolvedExperienceShell>({
    queryKey: experienceQueryKeys.shell(fp),
    queryFn: async ({ signal }) =>
      validateResolvedDescriptorI18n(await fetchExperienceShell(signal), { kind: 'experience-shell' }),
    enabled,
    ...experienceQueryDefaults,
  });
}

export function useExperienceRoutes(enabled = true) {
  const fp = useExperienceFingerprint();
  return useQuery<ResolvedRouteExperienceDescriptor[]>({
    queryKey: experienceQueryKeys.routes(fp),
    queryFn: async ({ signal }) =>
      validateResolvedDescriptorI18n(await fetchExperienceRoutes(signal), { kind: 'experience-routes' }),
    enabled,
    ...experienceQueryDefaults,
  });
}

export function useResolvedResource(
  resourceId: string,
  options: Partial<UseQueryOptions<ResolvedResourceExperienceDescriptor>> = {},
) {
  const fp = useExperienceFingerprint();
  return useQuery<ResolvedResourceExperienceDescriptor>({
    queryKey: experienceQueryKeys.resource(resourceId, fp),
    queryFn: async ({ signal }) =>
      validateResolvedDescriptorI18n(await fetchResolvedResource(resourceId, signal), {
        kind: 'resource',
        id: resourceId,
      }),
    enabled: resourceId.length > 0,
    ...experienceQueryDefaults,
    ...options,
  });
}

export function useResolvedTable(
  resourceId: string,
  options: Partial<UseQueryOptions<TableDescriptor>> = {},
) {
  const fp = useExperienceFingerprint();
  return useQuery<TableDescriptor>({
    queryKey: experienceQueryKeys.table(resourceId, fp),
    queryFn: async ({ signal }) =>
      validateResolvedDescriptorI18n(await fetchResolvedTable(resourceId, signal), {
        kind: 'table',
        id: resourceId,
      }),
    enabled: resourceId.length > 0,
    ...experienceQueryDefaults,
    ...options,
  });
}

export function useResolvedForm(
  resourceId: string,
  mode: FormMode,
  options: Partial<UseQueryOptions<FormDescriptor>> = {},
) {
  const fp = useExperienceFingerprint();
  return useQuery<FormDescriptor>({
    queryKey: experienceQueryKeys.form(resourceId, mode, fp),
    queryFn: async ({ signal }) =>
      validateResolvedDescriptorI18n(await fetchResolvedForm(resourceId, mode, signal), {
        kind: `form:${mode}`,
        id: resourceId,
      }),
    enabled: resourceId.length > 0,
    ...experienceQueryDefaults,
    ...options,
  });
}

export function useGenericResourceList<
  TItem extends Record<string, unknown> = Record<string, unknown>,
>(
  resourceId: string,
  query: GenericResourceListQuery = {},
) {
  const fp = useExperienceFingerprint();
  const descriptor = useResolvedResource(resourceId);
  const binding = descriptor.data?.bindings.list;
  const queryKey = useMemo(
    () => experienceQueryKeys.data(resourceId, query, fp),
    [query.filters, query.page, query.pageSize, query.search, query.sortDirection, query.sortField, resourceId, fp],
  );
  const dataQuery = useQuery<GenericResourceListResponse<TItem>, Error, readonly TItem[]>({
    queryKey,
    queryFn: ({ signal }) => {
      if (!binding) throw new Error(`Resource ${resourceId} has no executable list binding`);
      return listResolvedResource<TItem>(binding, query, signal);
    },
    select: (response) => response.items,
    enabled: Boolean(binding),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    ...dataQuery,
    isLoading: descriptor.isLoading || dataQuery.isLoading,
    isError: descriptor.isError || dataQuery.isError,
    error: descriptor.error ?? dataQuery.error,
    refetch: async () => {
      const [descriptorResult, dataResult] = await Promise.all([
        descriptor.refetch(),
        dataQuery.refetch(),
      ]);
      return dataResult.data ?? descriptorResult.data;
    },
  };
}

export function useGenericResourceRecord<
  TRecord extends Record<string, unknown> = Record<string, unknown>,
>(resourceId: string, id: string | undefined, mode: 'view' | 'edit' = 'view') {
  const fp = useExperienceFingerprint();
  const descriptor = useResolvedResource(resourceId);
  const binding = descriptor.data?.bindings.read;
  const recordQuery = useQuery<TRecord>({
    queryKey: [...experienceQueryKeys.record(resourceId, id ?? '', fp), mode],
    queryFn: ({ signal }) => {
      if (!binding || !id) throw new Error(`Resource ${resourceId} has no executable read binding`);
      return getResolvedResourceRecord<TRecord>(binding, id, mode, signal);
    },
    enabled: Boolean(binding && id),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    ...recordQuery,
    isLoading: descriptor.isLoading || recordQuery.isLoading,
    isError: descriptor.isError || recordQuery.isError,
    error: descriptor.error ?? recordQuery.error,
    refetch: async () => {
      const [descriptorResult, recordResult] = await Promise.all([
        descriptor.refetch(),
        recordQuery.refetch(),
      ]);
      return recordResult.data ?? descriptorResult.data;
    },
  };
}
